import { useQuery } from '@tanstack/react-query';
import { getCommunityById } from '../Redux/util/Apis/communities';

/**
 * Helper to convert day number to name
 */
const getDayName = dayNumber => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];
  return days[dayNumber] || 'Unknown';
};

/**
 * Formats raw community API response into display-friendly structure
 * @param {Object} community - Raw community data from API
 * @returns {Object} Formatted community data
 */
export const formatCommunityData = community => {
  if (!community) return null;

  return {
    // Basic Info
    id: community.id,
    name: community.name,
    website: community.website,
    phone: community.phone,
    email: community.email,
    webhookEmail: community.webhook_email,

    // Status
    isLive: community.is_live,

    // Units
    totalUnits: community.total_units,
    availableUnits: community.available_units,

    // Tour Types
    tourTypes: community.tour_types || [],

    // Address
    address: community.address
      ? {
          fullAddress: community.address.full_address,
          streetAddress: community.address.street_address,
          city: community.address.city,
          stateLong: community.address.state_long,
          stateShort: community.address.state_short,
          country: community.address.country,
          postalCode: community.address.postal_code
        }
      : null,

    // Office Hours
    officeHours: (community.office_hours || []).map(oh => ({
      status: oh.status,
      day: oh.day,
      dayName: getDayName(oh.day),
      availability: (oh.availability || []).map(slot => ({
        startTime: slot.start_time,
        endTime: slot.end_time
      }))
    })),

    // Links & Emails
    parserEmail: community.parser_email,
    applicationLink: community.application_link,
    selfGuidedTourLink: community.self_guided_tour_link,
    section8Response: community.section_8_response
  };
};

/**
 * Hook to fetch community settings data
 * @param {Object} options - Hook options
 * @param {string} options.communityId - The community/apartment ID
 * @returns {Object} React Query result with formatted data
 */
export const useCommunitySettings = (options = {}) => {
  const { communityId } = options;

  return useQuery({
    queryKey: ['communitySettings', communityId],
    queryFn: async () => {
      const result = await getCommunityById(communityId);

      if (result?.error) {
        throw new Error(result.message || 'Failed to fetch community settings');
      }

      return formatCommunityData(result);
    },
    enabled: !!communityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000 // 10 minutes
  });
};

export default useCommunitySettings;
